import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Calendar,
  Clock,
  Users,
  X,
  AlertCircle,
  CheckCircle,
  Edit,
  Trash2,
  Save,
  Building,
  Maximize,
  Minimize
} from 'lucide-react';
import axios from 'axios';

const TableReservationsDashboard = () => {
  const { user, token } = useAuth();
  const [tables, setTables] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [businessCurrentDate, setBusinessCurrentDate] = useState('');
  const [dateFilter, setDateFilter] = useState('today');

  // Modal states
  const [showDayDetailModal, setShowDayDetailModal] = useState(false);
  const [selectedTableDetail, setSelectedTableDetail] = useState(null);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showBlockSlotModal, setShowBlockSlotModal] = useState(false);
  const [slotToBlock, setSlotToBlock] = useState(null);
  const [showReserveModal, setShowReserveModal] = useState(false);
  const [reserveForm, setReserveForm] = useState({ guestCount: '', guestName: '' });
  const [isFullscreen, setIsFullscreen] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const primaryColor = 'var(--theme-primary)';

  useEffect(() => {
    if (token) {
      fetchBusinessInfo();
      fetchSettings();
      fetchTables();
      fetchCategories();
      fetchReservations();
    }
  }, [token]);

  // Handle fullscreen mode - hide header and nav
  useEffect(() => {
    if (isFullscreen) {
      document.body.classList.add('dashboard-fullscreen');
    } else {
      document.body.classList.remove('dashboard-fullscreen');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('dashboard-fullscreen');
    };
  }, [isFullscreen]);

  const fetchBusinessInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/business-info`);
      if (response.data && response.data.current_time) {
        const [datePart] = response.data.current_time.split('T');
        setBusinessCurrentDate(datePart);
        updateDateBasedOnFilter('today', datePart);
      }
    } catch (error) {
      console.error('Error fetching business info:', error);
    }
  };

  const updateDateBasedOnFilter = (filter, businessDateStr = businessCurrentDate) => {
    if (!businessDateStr) return;
    
    const baseDate = new Date(businessDateStr + 'T00:00:00');
    let targetDate = new Date(baseDate);
    
    switch (filter) {
      case 'today':
        // Keep baseDate
        break;
      case 'yesterday':
        targetDate.setDate(baseDate.getDate() - 1);
        break;
      case 'tomorrow':
        targetDate.setDate(baseDate.getDate() + 1);
        break;
      default:
        break;
    }
    
    setCurrentDate(targetDate);
  };

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    updateDateBasedOnFilter(filter);
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/table-settings/public`);
      console.log('⚙️ Fetched settings:', response.data);
      if (response.data.success && response.data.settings) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchTables = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/tables`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const tablesList = response.data.tables || [];
        console.log('🍽️ Fetched tables:', tablesList);
        setTables(tablesList);
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/table-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCategories(response.data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchReservations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/table-reservations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const reservationsList = response.data.reservations || [];
        console.log('📅 Fetched reservations:', reservationsList);
        setReservations(reservationsList);
      }
    } catch (error) {
      console.error('Error fetching reservations:', error);
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const generateTimeSlots = () => {
    if (!settings) {
      console.log('⚠️ Settings not loaded yet');
      return [];
    }
    
    const slots = [];
    const startTime = settings.start_time || '09:00';
    const endTime = settings.end_time || '22:00';
    const interval = 15; // 15-minute intervals for display
    
    console.log('🕐 Generating slots from', startTime, 'to', endTime);
    
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    let currentHour = startHour;
    let currentMin = startMin;
    
    while (currentHour < endHour || (currentHour === endHour && currentMin <= endMin)) {
      const timeStr = `${String(currentHour).padStart(2, '0')}:${String(currentMin).padStart(2, '0')}`;
      slots.push(timeStr);
      
      currentMin += interval;
      if (currentMin >= 60) {
        currentHour += Math.floor(currentMin / 60);
        currentMin = currentMin % 60;
      }
    }
    
    return slots;
  };

  const isTimeSlotBooked = (tableId, date, timeSlot) => {
    const dateStr = formatDateForAPI(date);
    
    for (const reservation of reservations) {
      if (reservation.assigned_table_id !== tableId || reservation.dropoff_date !== dateStr) {
        continue;
      }
      
      if (reservation.status === 'cancelled') {
        continue;
      }
      
      const resTime = reservation.dropoff_time;
      const duration = reservation.duration_minutes || settings?.reservation_duration || 60;
      
      // Calculate end time
      const [resHour, resMin] = resTime.split(':').map(Number);
      const resStartMinutes = resHour * 60 + resMin;
      const resEndMinutes = resStartMinutes + duration;
      
      const [slotHour, slotMin] = timeSlot.split(':').map(Number);
      const slotMinutes = slotHour * 60 + slotMin;
      
      // Check if this slot falls within the reservation
      if (slotMinutes >= resStartMinutes && slotMinutes < resEndMinutes) {
        return reservation;
      }
    }
    
    return null;
  };

  const getReservationsForTable = (tableId) => {
    const dateStr = formatDateForAPI(currentDate);
    return reservations.filter(r => 
      r.assigned_table_id === tableId && 
      r.dropoff_date === dateStr &&
      r.status !== 'cancelled'
    );
  };

  const handleTableCardClick = (table) => {
    console.log('🖱️ Table card clicked:', table);
    console.log('📅 Current date:', currentDate);
    console.log('📋 Available reservations:', reservations);
    const tableReservations = getReservationsForTable(table.id);
    console.log('🎯 Reservations for this table today:', tableReservations);
    
    setSelectedTableDetail({
      table,
      date: currentDate
    });
    setShowDayDetailModal(true);
  };

  const handleViewReservation = (reservation) => {
    console.log('👁️ Viewing reservation:', reservation);
    setSelectedReservation(reservation);
    const assignedTable = tables.find(t => t.id === reservation.assigned_table_id);
    const categoryId = assignedTable?.category_id || '';
    
    setEditForm({
      dropoff_date: reservation.dropoff_date,
      dropoff_time: reservation.dropoff_time,
      guest_count: reservation.guest_count,
      assigned_table_id: reservation.assigned_table_id,
      category_id: categoryId,
      status: reservation.status
    });
    setShowDayDetailModal(false);
    setShowReservationModal(true);
    setIsEditMode(false);
  };

  const handleBlockSlotClick = (table, date, timeSlot) => {
    const duration = settings?.reservation_duration || 60;
    setSlotToBlock({
      table,
      date,
      timeSlot,
      duration,
      availableSlots: 1,
      requestedSlots: Math.ceil(duration / 15),
      isPartialBlock: false
    });
    setShowBlockSlotModal(true);
  };

  const confirmBlockSlot = async () => {
    if (!slotToBlock) return;
    
    try {
      setLoading(true);
      
      const blockData = {
        reservation_type: 'table',
        dropoff_date: formatDateForAPI(slotToBlock.date),
        dropoff_time: slotToBlock.timeSlot,
        guest_count: 0,
        duration_minutes: slotToBlock.duration,
        assigned_table_id: slotToBlock.table.id,
        assigned_table_name: slotToBlock.table.table_name,
        status: 'blocked',
        form_details: {
          blocked_by_admin: true,
          reason: 'Admin blocked'
        }
      };
      
      const response = await axios.post(`${API_BASE_URL}/api/table-reservations`, blockData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        showMessage('success', `Slot ${slotToBlock.timeSlot} blocked successfully`);
        fetchReservations();
        setShowBlockSlotModal(false);
        setSlotToBlock(null);
      }
    } catch (error) {
      console.error('Error blocking slot:', error);
      showMessage('error', error.response?.data?.detail || 'Error blocking slot');
    } finally {
      setLoading(false);
    }
  };

  const confirmUnblockSlot = async () => {
    if (!slotToBlock || !slotToBlock.reservationId) return;
    
    try {
      setLoading(true);
      
      const response = await axios.delete(`${API_BASE_URL}/api/table-reservations/${slotToBlock.reservationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        showMessage('success', `Slot ${slotToBlock.timeSlot} unblocked successfully`);
        fetchReservations();
        setShowBlockSlotModal(false);
        setSlotToBlock(null);
      }
    } catch (error) {
      console.error('Error unblocking slot:', error);
      showMessage('error', error.response?.data?.detail || 'Error unblocking slot');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReservation = async (reservationId) => {
    if (!window.confirm('Are you sure you want to delete this reservation?')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API_BASE_URL}/api/table-reservations/${reservationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        showMessage('success', 'Reservation deleted successfully');
        fetchReservations();
        setShowReservationModal(false);
        setShowDayDetailModal(false);
      }
    } catch (error) {
      showMessage('error', 'Failed to delete reservation');
    }
  };

  const handleSaveReservation = async () => {
    try {
      setLoading(true);
      console.log('💾 Saving reservation with status:', editForm.status);
      
      const updateData = {
        status: editForm.status
      };
      
      const response = await axios.patch(
        `${API_BASE_URL}/api/table-reservations/${selectedReservation.id}`,
        updateData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        showMessage('success', 'Reservation updated successfully');
        fetchReservations();
        setIsEditMode(false);
        setShowReservationModal(false);
      }
    } catch (error) {
      console.error('Error updating reservation:', error);
      showMessage('error', error.response?.data?.detail || 'Failed to update reservation');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReservation = async () => {
    try {
      setLoading(true);
      console.log('✨ Creating reservation:', reserveForm);
      
      const reservationData = {
        reservation_type: 'table',
        dropoff_date: formatDateForAPI(slotToBlock.date),
        dropoff_time: slotToBlock.timeSlot,
        guest_count: parseInt(reserveForm.guestCount) || 1,
        duration_minutes: slotToBlock.duration,
        assigned_table_id: slotToBlock.table.id,
        assigned_table_name: slotToBlock.table.table_name,
        status: 'checked_in',
        form_details: {
          name: reserveForm.guestName || 'Walk-in Guest',
          created_by_admin: true
        }
      };
      
      const response = await axios.post(`${API_BASE_URL}/api/table-reservations`, reservationData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        showMessage('success', 'Reservation created successfully');
        fetchReservations();
        setShowReserveModal(false);
        setShowBlockSlotModal(false);
        setReserveForm({ guestCount: '', guestName: '' });
        setSlotToBlock(null);
      }
    } catch (error) {
      console.error('Error creating reservation:', error);
      showMessage('error', error.response?.data?.detail || 'Failed to create reservation');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes}${ampm}`;
  };

  return (
    <>
      <style>{`
        body.dashboard-fullscreen header {
          display: none !important;
        }
        body.dashboard-fullscreen main {
          padding-top: 0 !important;
        }
      `}</style>
      
      <div className={`min-h-screen ${isFullscreen ? 'bg-white' : 'bg-gray-50'}`}>
        {isFullscreen && (
          <div className="fixed top-4 right-4 z-50">
            <button
              onClick={() => setIsFullscreen(false)}
              className="p-3 bg-white text-gray-700 hover:text-gray-900 rounded-lg shadow-lg hover:shadow-xl transition-all"
              title="Exit Fullscreen"
            >
              <Minimize className="h-6 w-6" />
            </button>
          </div>
        )}
      
      <div className={`${isFullscreen ? 'w-full' : 'max-w-6xl mx-auto'} px-4 sm:px-6 lg:px-8 ${isFullscreen ? 'py-4' : 'py-8'}`}>
        
        <div className={`bg-white ${isFullscreen ? '' : 'rounded-xl shadow-lg'} overflow-hidden`}>
          {/* Header with Filter on Right */}
          {!isFullscreen && (
            <div className="bg-white border-b border-gray-200">
              <div className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Table Reservations
                    </h2>
                  </div>
                  
                  {/* Date Display and Filter */}
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-700 bg-green-100 px-3 py-1.5 rounded-md">
                      {currentDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                    <select
                      value={dateFilter}
                      onChange={(e) => handleDateFilterChange(e.target.value)}
                      className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-colors appearance-none pr-8"
                      style={{ 
                        backgroundColor: '#3b82f6',
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                        backgroundPosition: 'right 6px center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '14px'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                    >
                      <option value="today" style={{ backgroundColor: 'white', color: 'black' }}>Today</option>
                      <option value="yesterday" style={{ backgroundColor: 'white', color: 'black' }}>Yesterday</option>
                      <option value="tomorrow" style={{ backgroundColor: 'white', color: 'black' }}>Tomorrow</option>
                    </select>
                    <button
                      onClick={() => setIsFullscreen(true)}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                      title="Fullscreen View"
                    >
                      <Maximize className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Message Banner */}
          {message.text && (
            <div className={`mx-4 mt-4 px-4 py-3 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
              <p className="text-sm">{message.text}</p>
            </div>
          )}

          {/* Tables Grid */}
          <div className="p-6">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: primaryColor }}></div>
              </div>
            ) : tables.length === 0 ? (
              <div className="text-center py-12">
                <Building className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No tables found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tables.map((table) => {
              const tableReservations = getReservationsForTable(table.id);
              const category = categories.find(c => c.id === table.category_id);
              
              return (
                <div
                  key={table.id}
                  onClick={() => handleTableCardClick(table)}
                  className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all cursor-pointer border-2 border-transparent hover:border-blue-300 overflow-hidden"
                >
                  {/* Card Header */}
                  <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: primaryColor }}>
                    <h3 className="text-lg font-semibold text-white">{table.table_name}</h3>
                    <div className="flex items-center justify-between mt-1">
                      {category && (
                        <span className="text-xs text-white text-opacity-90">{category.category_name}</span>
                      )}
                      <span className="text-xs text-white text-opacity-90">Capacity: {table.capacity}</span>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-6">
                    {tableReservations.length === 0 ? (
                      <div className="text-center py-8">
                        <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
                        <p className="text-sm font-medium text-gray-700">All Clear</p>
                        <p className="text-xs text-gray-500 mt-1">No reservations today</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between mb-4">
                          <span className="text-sm font-semibold text-gray-700">
                            {tableReservations.length} {tableReservations.length === 1 ? 'Reservation' : 'Reservations'}
                          </span>
                          <span className="text-xs text-gray-500">Click card for slots</span>
                        </div>
                        
                        {tableReservations.slice(0, 3).map((res) => (
                          <div 
                            key={res.id} 
                            className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 hover:border-blue-300 transition-all cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewReservation(res);
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-gray-400" />
                                <span className="text-sm font-medium text-gray-900">
                                  {formatTime(res.dropoff_time)}
                                </span>
                              </div>
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                res.status === 'blocked' ? 'bg-gray-100 text-gray-700' :
                                res.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                                res.status === 'checked_in' ? 'bg-blue-100 text-blue-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {res.status === 'blocked' ? 'Blocked' : 
                                 res.status === 'confirmed' ? 'Confirmed' :
                                 res.status === 'checked_in' ? 'Checked In' : res.status}
                              </span>
                            </div>
                            {res.status !== 'blocked' && (
                              <div className="flex items-center gap-2 mt-2">
                                <Users className="h-3.5 w-3.5 text-gray-400" />
                                <span className="text-xs text-gray-600">
                                  {res.form_details?.first_name || res.form_details?.name || 'Guest'} • {res.guest_count} guests
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                        
                        {tableReservations.length > 3 && (
                          <div className="text-center pt-2">
                            <span className="text-xs text-gray-500">
                              +{tableReservations.length - 3} more
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
              </div>
            )}
          </div>
        </div>

        {/* Day Detail Modal */}
        {showDayDetailModal && selectedTableDetail && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0" style={{ backgroundColor: primaryColor }}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      {selectedTableDetail.table.table_name} - Time Slots
                    </h3>
                    <p className="text-sm text-white text-opacity-90 mt-0.5">
                      {selectedTableDetail.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowDayDetailModal(false)}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Time Slots Grid */}
              <div className="p-6 overflow-y-auto flex-1">
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                  {generateTimeSlots().map((timeSlot) => {
                    const reservation = isTimeSlotBooked(selectedTableDetail.table.id, selectedTableDetail.date, timeSlot);
                    const isBooked = reservation !== null;
                    const isBlocked = reservation?.status === 'blocked';
                    
                    return (
                      <div
                        key={timeSlot}
                        className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
                          isBooked
                            ? isBlocked
                              ? 'bg-gray-50 border-gray-300 hover:bg-gray-100'
                              : 'bg-red-50 border-red-200 hover:bg-red-100'
                            : 'bg-green-50 border-green-200 hover:bg-green-100'
                        }`}
                        onClick={() => {
                          if (isBlocked) {
                            setSlotToBlock({
                              table: selectedTableDetail.table,
                              date: selectedTableDetail.date,
                              timeSlot,
                              duration: reservation.duration_minutes || settings?.reservation_duration || 60,
                              reservationId: reservation.id,
                              isUnblocking: true
                            });
                            setShowBlockSlotModal(true);
                          } else if (isBooked) {
                            handleViewReservation(reservation);
                          } else {
                            handleBlockSlotClick(selectedTableDetail.table, selectedTableDetail.date, timeSlot);
                          }
                        }}
                        title={isBooked ? (isBlocked ? 'Blocked - Click to unblock' : 'Click to view reservation') : 'Click to block'}
                      >
                        <div className="text-center">
                          <div className="text-xs font-semibold text-gray-900">
                            {timeSlot}
                          </div>
                          <div className={`text-[10px] mt-0.5 font-medium ${
                            isBooked ? (isBlocked ? 'text-gray-600' : 'text-red-600') : 'text-green-600'
                          }`}>
                            {isBooked ? (isBlocked ? 'BLOCKED' : 'BOOKED') : 'Free'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-center gap-4 text-sm flex-wrap">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-green-50 border-2 border-green-200 rounded"></div>
                      <span className="text-gray-700">Available</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-red-50 border-2 border-red-200 rounded"></div>
                      <span className="text-gray-700">Booked</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-gray-50 border-2 border-gray-300 rounded"></div>
                      <span className="text-gray-700">Blocked</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 text-center mt-2">
                    Click slots to view details, block, or unblock
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Block/Unblock Slot Modal */}
        {showBlockSlotModal && slotToBlock && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
              <div className={`px-6 py-4 border-b border-gray-200 ${slotToBlock.isUnblocking ? 'bg-green-50' : 'bg-blue-50'}`}>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <AlertCircle className={`h-5 w-5 ${slotToBlock.isUnblocking ? 'text-green-600' : 'text-theme-blue'}`} />
                  {slotToBlock.isUnblocking ? 'Unblock Time Slot' : 'Manage Time Slot'}
                </h3>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Table:</span>
                      <span className="font-semibold text-gray-900">{slotToBlock.table.table_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Time:</span>
                      <span className="font-semibold text-gray-900">{slotToBlock.timeSlot}</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                {slotToBlock.isUnblocking ? (
                  <div className="space-y-3">
                    <button
                      onClick={confirmUnblockSlot}
                      className="w-full px-4 py-3 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg flex items-center justify-center gap-2"
                      disabled={loading}
                    >
                      <CheckCircle className="h-4 w-4" />
                      {loading ? 'Unblocking...' : 'Unblock Slot'}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        setShowReserveModal(true);
                      }}
                      className="w-full px-4 py-3 text-sm font-medium text-white rounded-lg flex items-center justify-center gap-2"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <Users className="h-4 w-4" />
                      Reserve
                    </button>
                    <button
                      onClick={confirmBlockSlot}
                      className="w-full px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center gap-2"
                      disabled={loading}
                    >
                      <X className="h-4 w-4" />
                      {loading ? 'Blocking...' : 'Block Slot'}
                    </button>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowBlockSlotModal(false);
                    setSlotToBlock(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reservation Detail Modal - Simple View */}
        {showReservationModal && selectedReservation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: primaryColor }}>
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">Reservation Details</h3>
                  <button
                    onClick={() => setShowReservationModal(false)}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto flex-1 space-y-4">
                {!isEditMode ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <div className="text-xs font-semibold text-blue-700 uppercase mb-2">Time</div>
                        <div className="text-sm font-semibold text-gray-900">{formatTime(selectedReservation.dropoff_time)}</div>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg">
                        <div className="text-xs font-semibold text-green-700 uppercase mb-2">Guests</div>
                        <div className="text-sm font-semibold text-gray-900">{selectedReservation.guest_count}</div>
                      </div>
                    </div>

                    <div className="p-4 bg-purple-50 rounded-lg">
                      <div className="text-xs font-semibold text-purple-700 uppercase mb-2">Status</div>
                      <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${
                        selectedReservation.status === 'blocked' ? 'bg-gray-100 text-gray-700' :
                        selectedReservation.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                        selectedReservation.status === 'checked_in' ? 'bg-blue-100 text-blue-700' :
                        selectedReservation.status === 'completed' ? 'bg-indigo-100 text-indigo-700' :
                        selectedReservation.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {selectedReservation.status === 'blocked' ? 'Blocked' : 
                         selectedReservation.status === 'confirmed' ? 'Confirmed' :
                         selectedReservation.status === 'checked_in' ? 'Checked In' :
                         selectedReservation.status === 'completed' ? 'Completed' :
                         selectedReservation.status === 'cancelled' ? 'Cancelled' :
                         selectedReservation.status}
                      </span>
                    </div>

                    {selectedReservation.form_details && Object.keys(selectedReservation.form_details).length > 0 && (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Customer Information</h4>
                        <div className="grid grid-cols-2 gap-3">
                          {Object.entries(selectedReservation.form_details)
                            .filter(([key]) => key !== 'guest_count')
                            .map(([key, value]) => (
                              <div key={key}>
                                <label className="text-xs font-medium text-gray-500">
                                  {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                </label>
                                <p className="text-sm text-gray-900 mt-0.5">{value || 'N/A'}</p>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        <strong>Note:</strong> You can update the status of this reservation below.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                      <select
                        value={editForm.status}
                        onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="checked_in">Checked In</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="no_show">No Show</option>
                      </select>
                    </div>

                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Reservation Details</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">Time:</span>
                          <span className="ml-2 font-medium">{formatTime(selectedReservation.dropoff_time)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Guests:</span>
                          <span className="ml-2 font-medium">{selectedReservation.guest_count}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                <div className="flex gap-2">
                  {!isEditMode && (
                    <button
                      onClick={() => handleDeleteReservation(selectedReservation.id)}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg flex items-center gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  {!isEditMode ? (
                    <>
                      <button
                        onClick={() => setIsEditMode(true)}
                        className="px-4 py-2 text-sm font-medium text-white rounded-lg flex items-center gap-2"
                        style={{ backgroundColor: primaryColor }}
                      >
                        <Edit className="h-4 w-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => setShowReservationModal(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Close
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setIsEditMode(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveReservation}
                        className="px-4 py-2 text-sm font-medium text-white rounded-lg flex items-center gap-2"
                        style={{ backgroundColor: primaryColor }}
                        disabled={loading}
                      >
                        <Save className="h-4 w-4" />
                        {loading ? 'Saving...' : 'Save Changes'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reserve Modal */}
        {showReserveModal && slotToBlock && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: primaryColor }}>
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">Create Reservation</h3>
                  <button
                    onClick={() => {
                      setShowReserveModal(false);
                      setReserveForm({ guestCount: '', guestName: '' });
                    }}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Table:</span>
                      <span className="font-semibold text-gray-900">{slotToBlock.table.table_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Time:</span>
                      <span className="font-semibold text-gray-900">{slotToBlock.timeSlot}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Guest Count <span className="text-gray-400">(Optional)</span>
                  </label>
                  <select
                    value={reserveForm.guestCount}
                    onChange={(e) => setReserveForm({ ...reserveForm, guestCount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                  >
                    <option value="">Select guest count</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                      <option key={num} value={num}>{num} {num === 1 ? 'Guest' : 'Guests'}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Guest Name <span className="text-gray-400">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={reserveForm.guestName}
                    onChange={(e) => setReserveForm({ ...reserveForm, guestName: e.target.value })}
                    placeholder="Enter guest name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-theme-blue"
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowReserveModal(false);
                    setReserveForm({ guestCount: '', guestName: '' });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateReservation}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg flex items-center gap-2"
                  style={{ backgroundColor: primaryColor }}
                  disabled={loading}
                >
                  <CheckCircle className="h-4 w-4" />
                  {loading ? 'Creating...' : 'Create Reservation'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default TableReservationsDashboard;
